export interface AsyncLimiter {
  run<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T>;
}

/** Limits concurrent tasks in FIFO order. Aborting discards only tasks that have not started. */
export function createAsyncLimiter(concurrency: number): AsyncLimiter {
  if (!Number.isSafeInteger(concurrency) || concurrency < 1) {
    throw new RangeError("Concurrency must be a positive safe integer.");
  }

  let active = 0;
  const pending: Array<() => void> = [];

  const drain = () => {
    while (active < concurrency && pending.length > 0) {
      pending.shift()?.();
    }
  };

  return {
    run<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        const discard = () => {
          const index = pending.indexOf(start);
          if (index >= 0) pending.splice(index, 1);
          signal?.removeEventListener("abort", discard);
          reject(new DOMException("Queued task was aborted.", "AbortError"));
        };
        const start = () => {
          signal?.removeEventListener("abort", discard);
          active += 1;
          Promise.resolve()
            .then(task)
            .then(resolve, reject)
            .finally(() => {
              active -= 1;
              drain();
            });
        };
        if (signal?.aborted) {
          discard();
          return;
        }
        signal?.addEventListener("abort", discard, { once: true });
        pending.push(start);
        drain();
      });
    },
  };
}
